import type { Identifier, XYCoord } from "dnd-core";
import type { FC } from "react";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";

import { ItemTypes } from "./ItemTypes";
import { MdDragIndicator } from "react-icons/md";

const style = {
  //   border: "1px dashed gray",
  // padding:
  //   marginBottom: ".5rem",
  //   backgroundColor: "white",
  height: "100%",
  cursor: "move",
};

export interface CardProps {
  id: any;
  text: string;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  children: any;
  right?: boolean;
  itemType: string;
  disabled?: boolean; // np. zablokowany szablon: bez przeciągania i bez uchwytu
}

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DraggableItem: FC<CardProps> = ({
  id,
  text,
  index,
  moveCard,
  right = false,
  itemType,
  disabled = false,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: itemType,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveCard(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: itemType,
    canDrag: !disabled,
    item: () => {
      return { id, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;

  drag(drop(ref));

  return (
    <div className={`p-2 ${isDragging ? "bg-[var(--sc-bg)]" : "bg-[var(--sc-surface)]"}`}>
      <div
        ref={ref}
        style={{ ...style, opacity, cursor: disabled ? "default" : "move" }}
        data-handler-id={handlerId}
        title={disabled ? undefined : "Przeciągnij, aby zmienić kolejność"}
      >
        {!disabled && <MdDragIndicator className={`${right ? "float-right" : ""}`} />}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
};
